import React from 'react';
import { Button } from '@/components/ui/button';
import { Archive, Copy, Eye, Lock, Play, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { StringHelper } from '@/helpers/string.helper';
import { Link } from '@tanstack/react-router';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
export const CampaignCard = ({ campaign, canClone, onView, onDelete, onChangeStatus, isUpdatingStatus, isDeleting, dateFormat, }) => {
    const { t } = useTranslation();
    const created = new Date(campaign.createdAt);
    const updated = new Date(campaign.updatedAt);
    return (<div className="rounded-xl border bg-card p-4">
            {/* Header row: Image + Name + View */}
            <div className="flex items-start gap-3">
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-muted flex items-center justify-center shrink-0">
                    {(() => {
            // Card grid shows a 64×64 avatar — same size as the table
            // row, so reuse the thumbnail variant. Falls back to the
            // original for legacy uploads cached before Liip landed.
            const src = campaign.imageThumbnailUrl ?? campaign.imageUrl;
            return src ? (<img src={src} alt={campaign.name} className="object-cover w-full h-full" loading="lazy" width={64} height={64}/>) : (<span className="text-muted-foreground text-xs">AD</span>);
        })()}
                </div>

                <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                            <p className="font-semibold truncate">{campaign.name}</p>
                            <p className="text-xs text-muted-foreground break-all">
                                ID: AD-{campaign.publicId.toUpperCase()}
                            </p>
                        </div>

                        <Button variant="ghost" size="icon" onClick={() => onView(campaign.publicId)} aria-label="View">
                            <Eye className="h-4 w-4"/>
                        </Button>
                    </div>
                </div>
            </div>

            {/* Body: Metrics + Dates */}
            <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-lg border p-3">
                    <p className="text-[11px] text-muted-foreground">{t('total_scans')}</p>
                    <p className="text-lg font-semibold tabular-nums">{campaign.totalScans ?? 0}</p>
                </div>

                <div className="rounded-lg border p-3">
                    <p className="text-[11px] text-muted-foreground">{t('status')}</p>
                    <p className={cn('text-sm font-semibold', StringHelper.getStatusTextStyles(campaign.status))}>
                        {t(campaign.status)}
                    </p>
                </div>

                <div className="col-span-2 rounded-lg border p-3 space-y-1">
                    <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">{t('created_at')}</span>
                        <span className="text-foreground/80">{format(created, dateFormat)}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">{t('last_updated')}</span>
                        <span className="text-foreground/80">{format(updated, dateFormat)}</span>
                    </div>
                </div>
            </div>

            {/* Footer: Status actions + Delete */}
            {/* Footer: Status actions + Clone + Delete */}
            <div className="mt-4 flex items-center justify-between gap-2 px-2">
                <div className="flex flex-wrap gap-2">
                    {(campaign.status === 'draft' || campaign.status === 'archived') && (<Button size="sm" variant="outline" className="border-emerald-500 hover:border-emerald-600 bg-emerald-200 hover:bg-emerald-100 text-black hover:text-black dark:border-emerald-300 dark:hover:border-emerald-400 dark:bg-emerald-200 dark:hover:bg-emerald-100" onClick={() => onChangeStatus(campaign.publicId, 'active')} disabled={isUpdatingStatus}>
                            {t('activate')}
                            <Play className="h-4 w-4"/>
                        </Button>)}

                    {campaign.status === 'active' && (<Button size="sm" variant="outline" className="border-amber-500 hover:border-amber-600 bg-amber-200 hover:bg-amber-100 text-black hover:text-black dark:border-amber-300 dark:hover:border-amber-400 dark:bg-amber-200 dark:hover:bg-amber-100" onClick={() => onChangeStatus(campaign.publicId, 'archived')} disabled={isUpdatingStatus}>
                            {t('archive')}
                            <Archive className="h-4 w-4"/>
                        </Button>)}

                    {/* Clone (Create from existing) */}
                    {canClone ? (<Button size="sm" variant="outline" asChild>
                            <Link to="/console/campaigns/new" search={{ cloneFrom: campaign.publicId }}>
                                {t('clone')}
                                <Copy className="h-4 w-4"/>
                            </Link>
                        </Button>) : (<Tooltip>
                            <TooltipTrigger asChild>
                                <Button size="sm" variant="outline">
                                    {t('clone')}
                                    <Lock className="h-4 w-4"/>
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>{t('features.pro_only.clone', { ns: 'glossary' })}</TooltipContent>
                        </Tooltip>)}
                </div>

                {/* Delete */}
                <Button variant="ghost" size="sm" className="text-destructive" onClick={() => onDelete(campaign.publicId)} disabled={isDeleting} aria-label="Delete">
                    {t('delete')}
                    <Trash2 className="h-4 w-4"/>
                </Button>
            </div>
        </div>);
};
CampaignCard.displayName = 'CampaignCard';
